/** @jsxImportSource @emotion/react */
import React from "react";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import Popover from "@mui/material/Popover";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronDown} from "@fortawesome/pro-regular-svg-icons";
import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";
import {isInIframe} from "js/dashboards/full-screen";

const UNSAVED_TOOLTIP = "As this will open in a new window, you need to save your changes before presenting";
const UNSAVED_LINK_TOOLTIP = "Save your changes to copy a link";

const PresentationSplitButton = ({onViewCurrent, onPresentFromTab, onCopyPresentationLink, presentationLinkUrl, presentDisabled = false, iframeUnsavedDisabled = false, copyLinkDisabled = false}) => {
  const {theme} = React.useContext(CustomThemeContext);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [linkPopoverAnchor, setLinkPopoverAnchor] = React.useState(null);
  const dropdownButtonRef = React.useRef(null);

  const handleDropdownOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const handleCopyLinkClick = () => {
    handleClose();
    if (isInIframe) {
      setLinkPopoverAnchor(dropdownButtonRef.current);
    } else {
      onCopyPresentationLink();
    }
  };

  // Auto-select the URL text when the popover opens so the user can copy immediately
  const urlInputRef = React.useCallback(node => {
    if (node) node.select();
  }, []);

  const greyBorder = `1px solid ${theme.palette.border.light}`;
  const greyBorderDark = `1px solid ${theme.palette.text.disabled || theme.palette.text.secondary}`;

  const sharedButtonSx = {
    textTransform: "uppercase",
    fontSize: "0.7rem",
    letterSpacing: "0.05em",
    boxShadow: "none",
    height: 30,
    border: greyBorder,
    color: theme.palette.text.secondary,
    // position + zIndex on hover lets the hovered button's full (4-sided) border render
    // above its neighbour, so the shared edge isn't clipped by the adjacent button.
    position: "relative",
    "&:hover": {boxShadow: "none", border: greyBorderDark, backgroundColor: "transparent", zIndex: 1}
  };

  const mainButtonDisabled = iframeUnsavedDisabled || presentDisabled;
  const mainButtonTooltip = iframeUnsavedDisabled
      ? UNSAVED_TOOLTIP
      : presentDisabled
          ? "Cannot present skipped tabs. Unskip to present."
          : "";

  return (
      <div style={{display: "flex", alignItems: "center"}}>
        <Tooltip title={mainButtonTooltip} arrow>
          {/* span wrapper so the tooltip still shows while the button is disabled */}
          <span style={{display: "inline-flex"}}>
            <Button
                variant="outlined"
                size="small"
                disabled={mainButtonDisabled}
                startIcon={<i className="bhi-desktop" style={{fontSize: "0.85rem"}} />}
                onClick={onPresentFromTab}
                data-test-id="present-button"
                sx={{...sharedButtonSx, borderRadius: "4px 0 0 4px", whiteSpace: "nowrap", marginRight: "-1px"}}>
              Present
            </Button>
          </span>
        </Tooltip>
        <Button
            ref={dropdownButtonRef}
            variant="outlined"
            size="small"
            onClick={handleDropdownOpen}
            data-test-id="presentation-dropdown-button"
            sx={{...sharedButtonSx, borderRadius: "0 4px 4px 0", minWidth: 0, padding: "0 8px"}}>
          <FontAwesomeIcon icon={faChevronDown} style={{fontSize: "0.65rem"}} />
        </Button>
        <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            PaperProps={{sx: {backgroundColor: theme.palette.background.card}}}>
          <Tooltip title={iframeUnsavedDisabled ? UNSAVED_TOOLTIP : ""} arrow>
            {/* span wrapper so the tooltip still shows while the menu item is disabled */}
            <span>
              <MenuItem
                  onClick={iframeUnsavedDisabled ? undefined : () => { onViewCurrent(); handleClose(); }}
                  disabled={iframeUnsavedDisabled}
                  data-test-id="menu-view-current-tab">
                <ListItemIcon style={{minWidth: 24}}>
                  <i className="bhi-overview" style={{fontSize: "0.8rem", color: theme.palette.text.main}} />
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{fontSize: "0.8rem"}}>View current tab</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Tooltip title={copyLinkDisabled ? UNSAVED_LINK_TOOLTIP : ""} arrow>
            <span>
              <MenuItem
                  onClick={copyLinkDisabled ? undefined : handleCopyLinkClick}
                  disabled={copyLinkDisabled}
                  data-test-id="menu-copy-presentation-link">
                <ListItemIcon style={{minWidth: 24}}>
                  <i className="bhi-link" style={{fontSize: "0.8rem", color: theme.palette.text.main}} />
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{fontSize: "0.8rem"}}>Copy link</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
        </Menu>
        <Popover
            open={Boolean(linkPopoverAnchor)}
            anchorEl={linkPopoverAnchor}
            onClose={() => setLinkPopoverAnchor(null)}
            anchorOrigin={{vertical: "bottom", horizontal: "left"}}
            transformOrigin={{vertical: "top", horizontal: "left"}}
            PaperProps={{sx: {backgroundColor: theme.palette.background.card, padding: "10px 12px"}}}>
          <p style={{fontSize: "0.75rem", color: theme.palette.text.secondary, marginBottom: 6}}>Copy the link below to share this presentation:</p>
          <input
              ref={urlInputRef}
              readOnly
              value={presentationLinkUrl || ""}
              style={{
                width: 420,
                fontSize: "0.8rem",
                padding: "6px 8px",
                border: `1px solid ${theme.palette.border.light}`,
                borderRadius: 4,
                background: theme.palette.background.default,
                color: theme.palette.text.primary,
                outline: "none",
              }}
          />
        </Popover>
      </div>
  );
};

export default PresentationSplitButton;
